import React from 'react';
import { Award, Clock, Heart, Shield } from 'lucide-react';
import { statistics } from '../data/mock';

const About = () => {
  return (
    <section id="o-nama" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                O nama
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-blue-500 mb-6"></div>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                <span className="font-bold text-gray-900">Obućar Tašner Laki</span> je majstorska 
                radionica sa dugogodišnjom tradicijom i iskustvom u popravci obuće, tašni i kofera.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Naša misija je da vašim omiljenim predmetima vratimo prvobitni sjaj i funkcionalnost. 
                Svaki komad tretiramo sa pažnjom i posvećenošću, koristeći tradicionalne tehnike 
                i moderne materijale.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Verujemo u kvalitet, trajnost i ličan pristup svakom klijentu. Vaše zadovoljstvo 
                je naša najveća nagrada.
              </p>
            </div>

            {/* Values */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Kvalitet</h4>
                  <p className="text-gray-600 text-sm">Garancija na sve popravke</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Brzina</h4>
                  <p className="text-gray-600 text-sm">Hitne popravke u istom danu</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Posvećenost</h4>
                  <p className="text-gray-600 text-sm">Lični pristup svakom klijentu</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Poverenje</h4>
                  <p className="text-gray-600 text-sm">Preko 50,000 zadovoljnih klijenata</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Stats */}
          <div className="relative">
            {/* Background decorative element */}
            <div className="absolute -inset-4 bg-gradient-to-br from-teal-100 via-blue-100 to-cyan-100 rounded-3xl opacity-50 blur-2xl"></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Naši rezultati
              </h3>
              
              <div className="space-y-6">
                {/* Stat 1 */}
                <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-xl p-6 border border-teal-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 font-medium mb-1">Popravki obuće</p>
                      <p className="text-4xl font-bold text-teal-600">{statistics.shoes.count}</p>
                    </div>
                    <div className="w-16 h-16 bg-teal-200 rounded-full flex items-center justify-center">
                      <span className="text-3xl">👞</span>
                    </div>
                  </div>
                </div>

                {/* Stat 2 */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 font-medium mb-1">Popravki tašni</p>
                      <p className="text-4xl font-bold text-blue-600">{statistics.bags.count}</p>
                    </div>
                    <div className="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center">
                      <span className="text-3xl">👜</span>
                    </div>
                  </div>
                </div>

                {/* Stat 3 */}
                <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-xl p-6 border border-cyan-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 font-medium mb-1">Popravki kofera</p>
                      <p className="text-4xl font-bold text-cyan-600">{statistics.suitcases.count}</p>
                    </div>
                    <div className="w-16 h-16 bg-cyan-200 rounded-full flex items-center justify-center">
                      <span className="text-3xl">🧳</span>
                    </div>
                  </div>
                </div>

                {/* Stat 4 */}
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl p-6 border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 font-medium mb-1">Godina iskustva</p>
                      <p className="text-4xl font-bold text-indigo-600">{statistics.experience.count}</p>
                    </div>
                    <div className="w-16 h-16 bg-indigo-200 rounded-full flex items-center justify-center">
                      <span className="text-3xl">⭐</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="mt-8 pt-8 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-600">
                  Poverenje naših klijenata je naša najveća vrednost
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
